import React, { useContext, useRef, useMemo, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { DeckContext } from '../../context/DeckContext';
import EdgeIndicator from './EdgeIndicator';
import BaseBlock from './BaseBlock';
import { formatFeetInches } from '../../utils/measurementUtils';
import EdgeLabel from '../../../../../../enums/edgeLabel';
import { DeckBoxProps } from '../../../../../../types/shared/props';
import { getResponsiveBlockFontSize } from '../../utils/responsiveHelpers';
import { useDesignSpanContext } from '../../context/DesignSpanContext';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';

const DeckBox: React.FC<DeckBoxProps> = ({
	label,
	block,
	onSelect,
	onUpdateBlock,
	setIsDragging,
}) => {
	const { state, dispatch } = useContext(DeckContext);
	const { scaleFactor } = state;
	const { maxSpanIn } = useDesignSpanContext();
	const { reface } = useQuoteState();

	const nodeRef = useRef<HTMLDivElement>(null); // Create a ref for the DOM node

	// Calculate responsive font size based on screen width
	const fontSize = useMemo(() => getResponsiveBlockFontSize(), []);

	// Determine if DeckBox should be frozen
	const isFrozen =
		state.selectedBlock &&
		(state.selectedBlock.type === 'Bumpout' ||
			state.selectedBlock.type === 'Stair' ||
			state.selectedBlock.type === 'Landing');

	const deckBoxWidth = block.displayDimensions.width * scaleFactor;
	const deckBoxHeight = block.displayDimensions.height * scaleFactor;

	useEffect(() => {
		let dropbeamsNeeded;

		if (maxSpanIn) {
			if (block.dimensions.depth > maxSpanIn && !reface) {
				dropbeamsNeeded = Math.ceil(block.dimensions.depth / maxSpanIn - 1);
			} else {
				dropbeamsNeeded = 0;
			}
		}

		if (dropbeamsNeeded == undefined) return;

		if (dropbeamsNeeded && !block.dropbeam?.isIncluded) {
			dispatch({
				type: 'SET_DROPBEAM_NEEDED',
				payload: {
					show: true,
					block: block,
					dropbeamsNeeded,
				},
			});
		} else if (!dropbeamsNeeded && block.dropbeam?.isIncluded) {
			dispatch({
				type: 'SET_DROPBEAM_NOT_NEEDED',
				payload: {
					show: true,
					block: block,
				},
			});
		}
	}, [block.dimensions.depth, maxSpanIn, reface]);

	return (
		<BaseBlock
			block={block}
			onSelect={onSelect}
			onUpdateBlock={onUpdateBlock}
			setIsDragging={setIsDragging}
			disabled={isFrozen}
			nodeRef={nodeRef}
		>
			<Box
				ref={nodeRef}
				style={{ position: 'absolute' }}
				sx={{
					transform: `rotate(${block.rotation}deg)`,
					width: deckBoxWidth,
					height: deckBoxHeight,
					bgcolor: '#8BC34A',
					cursor: 'pointer',
					position: 'absolute',
					zIndex: 10,
					overflow: 'visible',
					transition: 'transform 0.2s, box-shadow 0.2s',
					'&:hover': {
						transform: `rotate(${block.rotation}deg) scale(1.02)`,
						boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
					},
					'&:active': {
						transform: `rotate(${block.rotation}deg) scale(0.98)`,
						transition: 'transform 0.1s',
					},
					'@media (hover: none)': {
						'&:active': {
							transform: `rotate(${block.rotation}deg) scale(0.98)`,
							filter: 'brightness(1.1)',
						},
					},
				}}
				data-test={`deckbox-${block.id}`}
			>
				<Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
					{/* Edge Indicators */}
					{block.edges.map((edge) => (
						<EdgeIndicator
							key={edge.label}
							position={edge.label.toLowerCase()}
							label={edge.label}
							type={edge.type}
							length={edge.length}
							onSelectEdge={(label: EdgeLabel) => {
								dispatch({
									type: 'SELECT_EDGE',
									payload: { blockId: block.id, edgeLabel: label },
								});
							}}
							isSelected={
								state.selectedEdge?.blockId == block.id &&
								state.selectedEdge?.edgeLabel === edge.label
							}
							parentIsSelected={state.selectedBlock?.id === block.id}
						/>
					))}

					<Box
						position="absolute"
						left={0}
						top={0}
						display={'flex'}
						justifyContent={'center'}
						alignItems={'center'}
						width={'100%'}
						height={'100%'}
						sx={{
							cursor: 'pointer',
							// No onClick handler - let clicks bubble to Draggable naturally
						}}
					>
						<Typography
							variant="body1"
							lineHeight={1.15}
							color="rgba(0, 0, 0, 0.6)"
							noWrap
							zIndex={10}
							sx={{
								fontSize,
								pointerEvents: 'none', // Let clicks pass through text to Draggable
							}}
						>
							<p>
								<b>{label}</b>
							</p>
							{formatFeetInches(block.dimensions.depth)}
							<span className="px-0.5">&times;</span>
							{formatFeetInches(block.dimensions.width)}
						</Typography>
					</Box>
				</Box>
			</Box>
		</BaseBlock>
	);
};

export default DeckBox;
